import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export interface CsrfToken {
  token: string;
  expiry: number;
}

@Injectable({
  providedIn: 'root'
})
export class CsrfService {
  private http = inject(HttpClient);
  private readonly TOKEN_KEY = 'csrf_token';
  private readonly TOKEN_EXPIRY_KEY = 'csrf_token_expiry';
  private csrfToken$ = new BehaviorSubject<string | null>(null);

  constructor() {
    this.loadStoredToken();
  }

  /**
   * Get current CSRF token
   */
  getToken(): Observable<string> {
    const storedToken = this.getStoredToken();
    
    if (storedToken && this.isTokenValid(storedToken)) {
      this.csrfToken$.next(storedToken.token);
      return this.csrfToken$.asObservable().pipe(
        map(token => token || storedToken.token)
      );
    }

    // Token is expired or doesn't exist, fetch new one
    return this.fetchNewToken();
  }

  /**
   * Get CSRF token synchronously (use only if you're sure token exists)
   */
  getTokenSync(): string | null {
    return this.csrfToken$.value;
  }

  /**
   * Generate CSRF token for forms
   */
  generateFormToken(): string {
    return this.generateRandomToken();
  }

  /**
   * Validate CSRF token
   */
  validateToken(token: string): boolean {
    const storedToken = this.getStoredToken();
    return storedToken ? storedToken.token === token && this.isTokenValid(storedToken) : false;
  }

  /**
   * Get headers with CSRF token
   */
  getSecureHeaders(): Observable<HttpHeaders> {
    return this.getToken().pipe(
      map(token => new HttpHeaders({
        'X-CSRF-Token': token,
        'Content-Type': 'application/json'
      }))
    );
  }

  /**
   * Clear stored token
   */
  clearToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    this.csrfToken$.next(null);
  }

  /**
   * Refresh token
   */
  refreshToken(): Observable<string> {
    this.clearToken();
    return this.fetchNewToken();
  }

  private fetchNewToken(): Observable<string> {
    // In a real application, this would fetch from your backend
    // For now, we'll generate a client-side token for form protection
    const token = this.generateRandomToken();
    const expiry = Date.now() + (60 * 60 * 1000); // 1 hour

    this.storeToken({ token, expiry });
    this.csrfToken$.next(token);

    return this.csrfToken$.asObservable().pipe(
      map(() => token)
    );
  }

  private generateRandomToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  private loadStoredToken(): void {
    const storedToken = this.getStoredToken();
    if (storedToken && this.isTokenValid(storedToken)) {
      this.csrfToken$.next(storedToken.token);
    }
  }

  private getStoredToken(): CsrfToken | null {
    try {
      const token = localStorage.getItem(this.TOKEN_KEY);
      const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      
      if (token && expiry) {
        return {
          token,
          expiry: parseInt(expiry, 10)
        };
      }
    } catch (error) {
      console.warn('Error reading CSRF token from localStorage:', error);
    }
    
    return null;
  }

  private storeToken(csrfToken: CsrfToken): void {
    try {
      localStorage.setItem(this.TOKEN_KEY, csrfToken.token);
      localStorage.setItem(this.TOKEN_EXPIRY_KEY, csrfToken.expiry.toString());
    } catch (error) {
      console.warn('Error storing CSRF token to localStorage:', error);
    }
  }

  private isTokenValid(csrfToken: CsrfToken): boolean {
    return Date.now() < csrfToken.expiry;
  }
}